/**CORE LIBRARIES IMPORT */
import React from "react";
import { motion } from "framer-motion";

/**COMPONENTS / HOOKS IMPORT */
import VenuePhoto from "./VenuePhoto";
import useInViewOnce from "../hooks/useInViewOnce";

/**DATA / HOOKS IMPORT */
import { TBC, WEEKDAYS, directionsUrl } from "../data/site";
import useSiteSettings from "../hooks/useSiteSettings";

/** Weekly hours from the portal; falls back to the free-text hours, then "To be confirmed". */
export function OpeningHours({ week, text }) {
  const days = WEEKDAYS.filter(([k]) => week?.[k]);
  if (days.length) {
    return (
      <ul className="opening-hours">
        {days.map(([k, label]) => (
          <li key={k}><span>{label}</span><span>{week[k]}</span></li>
        ))}
      </ul>
    );
  }
  return text ? <span className="pre-line">{text}</span> : TBC;
}

/** "Find Us": template About layout with a smaller exterior photo. */
const ContactSection = () => {
  const [h2Ref, inView] = useInViewOnce(0.5);
  const settings = useSiteSettings();
  const hasHours = Boolean(settings.openingHours) || WEEKDAYS.some(([k]) => settings.openingHoursWeek?.[k]);

  const rows = [
    { label: "Location", value: <span className="address-text">{settings.address}</span> },
    { label: "Email", value: <a href={`mailto:${settings.email}`}>{settings.email}</a> },
    {
      label: "Phone",
      value: settings.phone ? <a href={`tel:${settings.phone.replace(/[^\d+]/g, "")}`}>{settings.phone}</a> : TBC,
      tbc: !settings.phone,
    },
    { label: "Opening hours", value: <OpeningHours week={settings.openingHoursWeek} text={settings.openingHours} />, tbc: !hasHours },
  ];

  return (
    <section id="contact" className="about-section contact-section" aria-labelledby="contact-heading" tabIndex={-1}>
      <div className="about-container">
        <div className="about-left">
          <h2 id="contact-heading" ref={h2Ref} className={`section-heading ${inView ? "in-view" : ""}`}>
            Find Us
          </h2>

          <motion.dl
            className="details-list contact-list"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {rows.map((row) => (
              <div key={row.label} className="details-row">
                <dt>{row.label}</dt>
                <dd className={row.tbc ? "is-tbc" : undefined}>{row.value}</dd>
              </div>
            ))}
          </motion.dl>

          <div className="hero-buttons section-buttons">
            <a href={directionsUrl(settings)} className="hero-btn" target="_blank" rel="noopener noreferrer">
              Get Directions<span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </div>

        <motion.div
          className="about-right"
          initial={{ opacity: 0, x: 50 }}
          animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <VenuePhoto slot="contact" sizes="(min-width: 992px) 560px, 100vw" className="venue-photo-small" />
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
